import { useEffect, useState, useCallback } from "react";
import { fetchJobs, fetchTrucks, fetchStats, fetchSourceBreakdown, updateJobStatus } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Briefcase, Truck as TruckIcon, CheckCircle2, AlertTriangle, Radio, Wrench, X } from "lucide-react";
import JobCard from "../components/JobCard";
import DispatchMap from "../components/DispatchMap";
import AssignDialog from "../components/AssignDialog";
import CancelOnRouteDialog from "../components/CancelOnRouteDialog";
import EditJobSheet from "../components/EditJobSheet";
import JobDetailSheet from "../components/JobDetailSheet";
import BookForLaterDialog from "../components/BookForLaterDialog";
import TruckQueue from "../components/TruckQueue";
import GlobalSearch from "../components/GlobalSearch";
import { TRUCK_STATUS_COLORS } from "../lib/constants";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import OnboardingBanner from "../components/OnboardingBanner";
import SampleDataBanner from "../components/SampleDataBanner";

const hasCoord = (lat, lng) =>
  typeof lat === "number" && typeof lng === "number" && (lat !== 0 || lng !== 0);

function KpiSeg({ label, value, sub, icon: Icon, accent, onClick, testid }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      className={cn(
        "flex-1 min-w-0 h-full px-2.5 sm:px-3 xl:px-4 flex items-center gap-2 xl:gap-2.5 text-left",
        onClick && "hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors cursor-pointer",
      )}
      data-testid={testid}
    >
      {Icon && (
        <div className={cn("h-8 w-8 xl:h-9 xl:w-9 rounded-md flex items-center justify-center shrink-0", accent)}>
          <Icon className="h-4 w-4 xl:h-4.5 xl:w-4.5" strokeWidth={2.4} />
        </div>
      )}
      <div className="min-w-0">
        <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400 leading-none">{label}</div>
        <div className="font-display text-lg sm:text-xl xl:text-2xl font-black text-slate-900 dark:text-slate-100 leading-tight">
          {value}
        </div>
        {sub && <div className="text-[10px] text-slate-500 leading-none truncate">{sub}</div>}
      </div>
    </Tag>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [stats, setStats] = useState({});
  const [intake, setIntake] = useState({ aa: 0, sn: 0, manual: 0, total: 0 });
  const [assignFor, setAssignFor] = useState(null);
  const [editJob, setEditJob] = useState(null);
  const [detailJob, setDetailJob] = useState(null);
  const [corJob, setCorJob] = useState(null);
  const [bookForLaterJob, setBookForLaterJob] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [mapFocus, setMapFocus] = useState(null);

  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [j, t, s, b] = await Promise.all([
        fetchJobs(), fetchTrucks(), fetchStats(),
        fetchSourceBreakdown("today").catch(() => null),
      ]);
      setJobs(j);
      setTrucks(t);
      setStats(s);
      if (b) setIntake(b);
    } catch (e) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    refresh();
    const onEvt = () => refresh();
    window.addEventListener("jobs:refresh", onEvt);
    const t = setInterval(refresh, 15000);
    return () => {
      window.removeEventListener("jobs:refresh", onEvt);
      clearInterval(t);
    };
  }, [refresh]);

  // Full-screen live map — opened via topbar button, closed with ESC / X.
  useEffect(() => {
    const openEvt = () => setShowMap(true);
    window.addEventListener("livemap:open", openEvt);
    return () => window.removeEventListener("livemap:open", openEvt);
  }, []);
  useEffect(() => {
    if (!showMap) return;
    const onKey = (e) => { if (e.key === "Escape") setShowMap(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showMap]);

  const advance = async (job, next) => {
    try {
      await updateJobStatus(job.id, next);
      toast.success(`Job marked ${next.replace("_", " ")}`);
      refresh();
    } catch {
      toast.error("Update failed");
    }
  };

  const cancel = async (job) => {
    try {
      await updateJobStatus(job.id, "cancelled");
      toast.success("Job cancelled");
      refresh();
    } catch {
      toast.error("Failed");
    }
  };

  const pending = jobs.filter((j) => j.status === "pending");
  // "In Progress" column = jobs awaiting work, OR active jobs that have
  // somehow lost their truck assignment (so they don't disappear from the
  // dispatcher's view). Once a driver presses "Start Work" on an assigned
  // job, it leaves this column and lives only on its truck's Fleet card.
  // `accepted` (Feb 2026): after the Aug 2026 driver-flow refit a driver's
  // first tap changes status → `accepted` before they hit Next → `en_route`.
  // Include it here so the dispatcher doesn't lose sight of the job for the
  // 20-40s the driver holds an accepted job before heading out.
  const active = jobs.filter((j) => {
    if (["assigned", "accepted", "en_route"].includes(j.status)) return true;
    if (["on_site", "awaiting_partner_close"].includes(j.status) && !j.assigned_truck_id) return true;
    return false;
  });

  return (
    <div className="p-3 sm:p-4 xl:p-6 space-y-4" data-testid="dashboard-page">
      <OnboardingBanner />
      <SampleDataBanner />
      {/* Tablet-only global search — replaces the KPI bar below dtop
       * (1281 px) so a dispatcher on the 10" tablet in the yard can
       * search by rego, tap a result, and land on the job to add
       * photos. The topbar's own search hides at the same breakpoint
       * so this becomes the hero. `dtop` is a custom breakpoint
       * (see tailwind.config.js) — the built-in `xl` fires AT 1280 px
       * which is exactly the tablet width, so we'd flicker at that
       * edge. `dtop` fires at 1281 px = clean split. */}
      <div className="dtop:hidden" data-testid="dashboard-search-tablet">
        <GlobalSearch size="lg" testidPrefix="dashboard" />
      </div>

      {/* KPI bar — desktop only. Open Jobs · Active Trucks · Completed
       * Today · Urgent · Today's Intake. Hidden on tablet because the
       * yard workflow prioritises search over glanceable stats. */}
      <div
        className="hidden dtop:flex bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md h-[56px] xl:h-[68px] items-stretch divide-x divide-slate-200 dark:divide-slate-700 overflow-x-auto"
        data-testid="kpi-bar"
      >
        <KpiSeg
          label="Open Jobs"
          value={stats.open_jobs ?? "—"}
          sub={`${stats.pending_jobs ?? 0} pending assignment`}
          icon={Briefcase}
          accent="bg-blue-100 text-blue-700"
          testid="kpi-open-jobs"
        />
        <KpiSeg
          label="Active Trucks"
          value={`${stats.busy_trucks ?? 0} / ${stats.active_trucks ?? 0}`}
          sub="On a job / on-duty"
          icon={TruckIcon}
          accent="bg-emerald-100 text-emerald-700"
          testid="kpi-trucks"
        />
        <KpiSeg
          label="Completed Today"
          value={stats.completed_today ?? 0}
          sub="Closed calls"
          icon={CheckCircle2}
          accent="bg-teal-100 text-teal-700"
          testid="kpi-completed"
        />
        <KpiSeg
          label="Urgent"
          value={stats.urgent ?? 0}
          sub="Open urgent calls"
          icon={AlertTriangle}
          accent="bg-rose-100 text-rose-700"
          testid="kpi-urgent"
        />
        <KpiSeg
          label="Today's Intake"
          value={intake.total ?? 0}
          sub={`AA ${intake.aa ?? 0} · FR ${intake.sn ?? 0} · Other ${intake.manual ?? 0}`}
          icon={Radio}
          accent="bg-amber-100 text-amber-700"
          onClick={() => nav("/history?range=today")}
          testid="kpi-intake"
        />
      </div>

      {/* 60 / 40 split: Job Queue (2-col dense cards) | Fleet Status.
       * Tablet fix (Feb 2026 — 10" 1280x800): trigger the split at `lg:`
       * (1024 px) instead of `xl:` (1280 px) so tablets in landscape
       * actually get the two-column dispatcher layout instead of the
       * stacked mobile fallback. */}
      <div className="grid grid-cols-5 gap-3 xl:gap-4">
        {/* Job queue — LH 60% */}
        <section className="col-span-5 lg:col-span-3 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-bold tracking-tight">Job Queue</h2>
            <span className="uppercase-label">{pending.length} pending</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="job-queue">
            {loading && <div className="text-sm text-slate-500">Loading…</div>}
            {!loading && pending.length === 0 && (
              <div className="lg:col-span-2 bg-white border border-dashed border-slate-300 rounded-md p-8 text-center text-sm text-slate-500">
                No pending jobs. Pull from partner feed or create one.
              </div>
            )}
            {pending.map((j) => (
              <JobCard
                key={j.id}
                job={j}
                trucks={trucks}
                dense
                onAssign={(jj) => setAssignFor(jj)}
                onEdit={(jj) => setEditJob(jj)}
                onCancel={cancel}
                onBookForLater={(jj) => setBookForLaterJob(jj)}
                onCardClick={(jj) => setDetailJob(jj)}
              />
            ))}

            {active.length > 0 && (
              <>
                <div className="lg:col-span-2 pt-2 flex items-center justify-between">
                  <h3 className="font-display text-base font-bold text-slate-700">In Progress</h3>
                  <span className="uppercase-label">{active.length}</span>
                </div>
                {active.map((j) => (
                  <JobCard
                    key={j.id}
                    job={j}
                    trucks={trucks}
                    dense
                    onAssign={(jj) => setAssignFor(jj)}
                    onAdvance={advance}
                    onEdit={(jj) => setEditJob(jj)}
                    onCancel={cancel}
                    onCancelOnRoute={(jj) => setCorJob(jj)}
                    onCardClick={(jj) => setDetailJob(jj)}
                  />
                ))}
              </>
            )}
          </div>
        </section>

        {/* Fleet status — RH 40% */}
        <section className="col-span-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-display text-lg font-bold tracking-tight">Fleet Status</h2>
            <span className="uppercase-label">{trucks.length} trucks</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-3 items-start">
            {trucks.map((t) => {
              const truckJobs = jobs
                .filter(
                  (j) => j.assigned_truck_id === t.id && !["completed", "cancelled"].includes(j.status)
                )
                .sort((a, b) => (a.queue_position || 99) - (b.queue_position || 99));
              const c = TRUCK_STATUS_COLORS[t.status] || TRUCK_STATUS_COLORS.offline;
              return (
                <div
                  key={t.id}
                  className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-2.5"
                  data-testid={`fleet-card-${t.id}`}
                >
                  <div className="flex items-center justify-between gap-1.5">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <div className="h-7 w-7 rounded-md bg-slate-900 dark:bg-slate-700 flex items-center justify-center shrink-0">
                        <Wrench className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-display font-bold text-sm text-slate-900 dark:text-slate-100 leading-tight flex items-center gap-1 truncate">
                          <span className="truncate">{t.name}</span>
                          {truckJobs.length > 0 && (
                            <span
                              className="bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 text-[9px] font-black px-1 py-px rounded-full leading-none shrink-0"
                              data-testid={`fleet-queue-count-${t.id}`}
                              title={`${truckJobs.length} job${truckJobs.length === 1 ? "" : "s"} in queue`}
                            >
                              {truckJobs.length}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{t.driver || "—"}</div>
                      </div>
                    </div>
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider shrink-0",
                        c.bg,
                        c.text
                      )}
                    >
                      <span className={cn("h-1.5 w-1.5 rounded-full", c.dot)} />
                      {t.status}
                    </span>
                  </div>

                  {truckJobs.length > 0 && (
                    <div className="mt-2.5">
                      <TruckQueue
                        truck={t}
                        queue={truckJobs}
                        onChange={refresh}
                        onReassign={(j) => setAssignFor(j)}
                        onOpenDetail={(j) => setDetailJob(j)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* Full-screen live map overlay — ESC or X to exit. */}
      {showMap && (
        <div className="fixed inset-0 z-[1000] bg-slate-950" data-testid="fullscreen-map">
          <DispatchMap
            jobs={jobs.filter((j) => j.status !== "completed" && j.status !== "cancelled")}
            trucks={trucks}
            focus={mapFocus}
          />
          <div className="absolute top-3 left-3 z-[1100] rounded-md bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-700 shadow px-3 py-1.5 text-xs font-bold text-slate-700 dark:text-slate-200">
            LIVE MAP · press <kbd className="px-1 py-0.5 rounded bg-slate-200 dark:bg-slate-700 font-mono">ESC</kbd> to exit
          </div>
          <button
            onClick={() => setShowMap(false)}
            className="absolute top-3 right-3 z-[1100] h-9 w-9 rounded-md bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-700 shadow flex items-center justify-center hover:bg-rose-50 dark:hover:bg-rose-950/40"
            title="Close map (ESC)"
            data-testid="fullscreen-map-close"
            aria-label="Close full-screen map"
          >
            <X className="h-4 w-4 text-slate-700 dark:text-slate-200" strokeWidth={3} />
          </button>

          {/* Fleet locator box — click a truck to centre the map on it. */}
          <div
            className="absolute right-3 top-16 z-[1100] w-56 max-h-[65vh] overflow-y-auto rounded-md bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-700 shadow-lg backdrop-blur"
            data-testid="fullscreen-map-fleet"
          >
            <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Fleet · tap to locate
            </div>
            {trucks.map((t) => {
              const c = TRUCK_STATUS_COLORS[t.status] || TRUCK_STATUS_COLORS.offline;
              const locatable = hasCoord(t.lat, t.lng);
              return (
                <button
                  key={t.id}
                  onClick={() => locatable && setMapFocus({ lat: t.lat, lng: t.lng, ts: Date.now() })}
                  disabled={!locatable}
                  className={cn(
                    "w-full text-left px-3 py-2 flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 last:border-b-0",
                    locatable ? "hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer" : "opacity-50 cursor-default",
                  )}
                  data-testid={`map-fleet-item-${t.id}`}
                  title={locatable ? "Centre map on this truck" : "No GPS position"}
                >
                  <span className={cn("h-2 w-2 rounded-full shrink-0", c.dot)} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-bold text-slate-800 dark:text-slate-100 truncate">{t.name}</span>
                    <span className="block text-[10px] text-slate-500 truncate">{t.driver || "—"}{!locatable && " · no GPS"}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <AssignDialog
        job={assignFor}
        trucks={trucks}
        jobs={jobs}
        open={!!assignFor}
        onOpenChange={(o) => !o && setAssignFor(null)}
        onAssigned={refresh}
      />

      <EditJobSheet
        job={editJob}
        open={!!editJob}
        onOpenChange={(o) => !o && setEditJob(null)}
        onSaved={refresh}
      />

      <JobDetailSheet
        job={detailJob}
        open={!!detailJob}
        onOpenChange={(o) => !o && setDetailJob(null)}
        truckById={Object.fromEntries(trucks.map((t) => [t.id, t]))}
      />

      <CancelOnRouteDialog
        open={!!corJob}
        job={corJob}
        onOpenChange={(o) => !o && setCorJob(null)}
        onDone={refresh}
      />

      <BookForLaterDialog
        open={!!bookForLaterJob}
        job={bookForLaterJob}
        onOpenChange={(o) => !o && setBookForLaterJob(null)}
        onBooked={refresh}
      />
    </div>
  );
}
